/**
 * Session List Component
 * Displays a list of user sessions with device info and actions
 */

import {
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  MapPin,
  Clock,
  LogOut,
  Shield,
  AlertTriangle,
} from 'lucide-react'
import type { Session } from '@/types/session.types'
import { formatRelativeTime, getSessionStatusDisplayName } from '@/types/session.types'

// ============================================================================
// TYPES
// ============================================================================

export interface SessionListProps {
  sessions: Session[]
  currentSessionId?: string
  onTerminate?: (sessionId: string) => void
  onTerminateAll?: () => void
  isLoading?: boolean
  showTerminateAll?: boolean
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getDeviceIcon(deviceType?: string) {
  switch (deviceType?.toLowerCase()) {
    case 'mobile':
      return Smartphone
    case 'tablet':
      return Tablet
    default:
      return Monitor
  }
}

function getDeviceLabel(session: Session): string {
  const parts: string[] = []
  if (session.browser && !/^unknown/i.test(session.browser)) parts.push(session.browser)
  if (session.os && !/^unknown/i.test(session.os)) parts.push(session.os)
  return parts.length > 0 ? parts.join(' on ') : 'Device details unavailable'
}

function getLocationLabel(session: Session): string {
  const parts: string[] = []
  if (session.city) parts.push(session.city)
  if (session.country) parts.push(session.country)
  return parts.length > 0 ? parts.join(', ') : 'Location unavailable'
}

// ============================================================================
// SESSION CARD COMPONENT
// ============================================================================

interface SessionCardProps {
  session: Session
  isCurrentSession: boolean
  onTerminate?: (sessionId: string) => void
}

function SessionCard({ session, isCurrentSession, onTerminate }: SessionCardProps) {
  const DeviceIcon = getDeviceIcon(session.deviceType)
  const isActive = session.status === 'active'

  return (
    <div
      className={`card card-elevated p-4 hover:shadow-lg transition ${
        isCurrentSession
          ? 'border-2 border-primary-400 bg-primary-50/50 dark:bg-primary-900/20'
          : ''
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Device Icon */}
        <div
          className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isActive ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-gray-100 dark:bg-gray-800'
          }`}
        >
          <DeviceIcon
            className={`w-6 h-6 ${
              isActive
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-gray-400 dark:text-gray-500'
            }`}
          />
        </div>

        {/* Session Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-text-primary truncate">{getDeviceLabel(session)}</h3>
            {isCurrentSession && (
              <span className="badge badge-primary badge-sm flex-shrink-0">Current</span>
            )}
            {!isActive && (
              <span className="badge badge-ghost badge-sm flex-shrink-0 capitalize">
                {getSessionStatusDisplayName(session.status)}
              </span>
            )}
          </div>

          {/* Location & IP */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-text-secondary">
            <div className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>{getLocationLabel(session)}</span>
            </div>
            {session.ipAddress && (
              <div className="flex items-center gap-1">
                <Globe className="w-3.5 h-3.5" />
                <span>{session.ipAddress}</span>
              </div>
            )}
          </div>

          {/* Timestamps */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-text-tertiary">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>Last active {formatRelativeTime(session.lastActivityAt)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              <span>Started {formatRelativeTime(session.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        {isActive && !isCurrentSession && onTerminate && (
          <button
            type="button"
            onClick={() => onTerminate(session.sessionId)}
            className="btn btn-ghost btn-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="End this session"
            aria-label="End this session"
          >
            <LogOut className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SessionList({
  sessions,
  currentSessionId,
  onTerminate,
  onTerminateAll,
  isLoading = false,
  showTerminateAll = true,
}: SessionListProps) {
  const activeSessions = sessions.filter(s => s.status === 'active')
  const otherActiveSessions = activeSessions.filter(s => s.sessionId !== currentSessionId)

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="card card-elevated p-4">
            <div className="flex items-start gap-4">
              <div className="skeleton w-12 h-12 rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-5 rounded w-1/3" />
                <div className="skeleton h-4 rounded w-1/2" />
                <div className="skeleton h-3 rounded w-2/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Empty state
  if (sessions.length === 0) {
    return (
      <div className="card card-elevated p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-surface-overlay flex items-center justify-center mx-auto mb-4">
          <Monitor className="w-8 h-8 text-text-tertiary" />
        </div>
        <p className="text-lg font-medium text-text-primary mb-2">No sessions found</p>
        <p className="text-text-secondary">Try adjusting your filters</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with Terminate All */}
      {showTerminateAll && otherActiveSessions.length > 0 && onTerminateAll && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-secondary">
            {activeSessions.length} active session{activeSessions.length !== 1 ? 's' : ''}
          </p>
          <button
            type="button"
            onClick={onTerminateAll}
            className="btn btn-ghost btn-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 gap-2"
          >
            <AlertTriangle className="w-4 h-4" />
            End All Other Sessions
          </button>
        </div>
      )}

      {/* Session Cards */}
      <div className="space-y-3">
        {sessions.map(session => (
          <SessionCard
            key={session.sessionId}
            session={session}
            isCurrentSession={session.sessionId === currentSessionId}
            onTerminate={onTerminate}
          />
        ))}
      </div>
    </div>
  )
}
